import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";

interface UseExpertWalletLevelProgressOptions {
  actingOrgId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useExpertWalletLevelProgress(options: UseExpertWalletLevelProgressOptions) {
  const api = useApi();
  const actingOrgId = computed(() => unref(options.actingOrgId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(actingOrgId.value));

  const query = useQuery({
    queryKey: computed(() => ["expert", "wallet", actingOrgId.value, "level"]),
    queryFn: async () => {
      if (!isPositiveId(actingOrgId.value)) {
        throw new Error("actingOrgId is required to load expert wallet level progress");
      }

      return await api.expert.wallet.getLevelProgress({
        query: { acting_org_id: actingOrgId.value },
      });
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return {
    currentLevel: computed(() => query.data.value?.current_level ?? null),
    nextLevel: computed(() => query.data.value?.next_level ?? null),
    lifetimeGrossCents: computed(() => query.data.value?.lifetime_gross_cents ?? 0),
    remainingGrossCents: computed(() => query.data.value?.remaining_gross_cents ?? null),
    currency: computed(() => query.data.value?.currency ?? null),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    query,
  };
}
